if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.multi_item_request = false;
    }

    connectedCallback() {
      if (this._productFormInitialized) return;
      this.form = this.querySelector('form');
      if (!this.form) return;
      const idInput = this.form.querySelector('[name=id]');
      if (idInput) idInput.disabled = false;
      this._boundSubmitHandler = this.onSubmitHandler.bind(this);
      this.form.addEventListener('submit', this._boundSubmitHandler);
      this.submitButton = this.querySelector('[type="submit"]');
      this.cartNotification = document.getElementById('cart-notification');
      this.cartNotificationItem = document.getElementById('cart-notification-item');
      this._productFormInitialized = true;
      this.validationRoot = this.dataset.productId ? this.closest('.section--main-product') : null;
      if (this.validationRoot) {
        this.validationAbort = new AbortController();
        this.pendingUploads = new WeakMap();
        const options = { capture: true, signal: this.validationAbort.signal };
        // Run before app button handlers, but leave successful clicks entirely to apps.
        this.validationRoot.addEventListener('click', event => {
          const button = event.target.closest('[name="add"], [dpo_add2cart], [data-ph-cart-clone]');
          if (!button || button.closest('product-form') !== this) return;
          if (!this.validateCustomizations()) { event.preventDefault(); event.stopImmediatePropagation(); }
        }, options);
        this.validationRoot.addEventListener('submit', event => {
          if (event.target === this.form && !this.validateCustomizations()) {
            event.preventDefault(); event.stopImmediatePropagation();
          }
        }, options);
        this.validationRoot.addEventListener('change', event => {
          const field = event.target.closest('#photouploadapp [id^="selBtn_"]');
          if (field && event.target.type === 'file') {
            if (event.target.files?.length) this.pendingUploads.set(field, JSON.stringify(this.uploadProperties(field)));
            else this.pendingUploads.delete(field);
          }
          this.uploadNotice?.remove();
        }, { signal: this.validationAbort.signal });
        this.validationRoot.addEventListener('cancel', event => {
          const field = event.target.closest('#photouploadapp [id^="selBtn_"]');
          if (field && event.target.type === 'file') this.pendingUploads.delete(field);
        }, options);
      }

    }

    disconnectedCallback() {
      this.validationAbort?.abort();
      this.uploadNotice?.remove();
      if (this.form && this._boundSubmitHandler) {
        this.form.removeEventListener('submit', this._boundSubmitHandler);
      }
      this._productFormInitialized = false;
    }

    uploadProperties(field) {
      const uid = field.id.slice('selBtn_'.length);
      const name = window.UPLOADCARE_DATA?.[uid]?.fieldname || field.dataset.phFieldname;
      if (!name) return [];
      const holders = [...this.form.querySelectorAll('[data-main]')].filter(node => {
        const marker = node.dataset.main;
        return marker.startsWith(uid) && /^\d+$/.test(marker.slice(uid.length));
      });
      return [field, ...holders].flatMap(node => [...node.querySelectorAll('[name^="properties["]')])
        .filter(input => {
          if (input.disabled || !input.value || input.tagName !== 'INPUT' || input.hasAttribute('data-ph-textarea')) return false;
          const key = input.name.slice(11, -1);
          // Uploadly uses the field name, or a numeric suffix for multiple files.
          return key === name || (key.startsWith(`${name} `) && /^[1-9]\d*$/.test(key.slice(name.length + 1)));
        })
        .map(input => [input.name, input.value]);
    }

    validateCustomizations() {
      if (!this.validationRoot) return true;
      this.uploadNotice?.remove();
      this.handleErrorMessage();
      const fields = [...this.validationRoot.querySelectorAll('#photouploadapp [id^="selBtn_"]')].filter(field => {
        for (let node = field; node; node = node.parentElement) {
          const style = getComputedStyle(node);
          if (node.hidden || style.display === 'none' || style.visibility === 'hidden') return false;
        }
        return true;
      });
      const invalid = fields.find(field => {
        const properties = this.uploadProperties(field);
        const previous = this.pendingUploads.get(field);
        if (previous !== undefined && properties.length && previous !== JSON.stringify(properties)) this.pendingUploads.delete(field);
        const status = field.querySelector('.uploadcare--widget')?.getAttribute('data-status');
        return !properties.length || this.pendingUploads.has(field) || ['uploading', 'error'].includes(status) ||
          !!field.querySelector('[aria-busy="true"]');
      });
      // DPO owns its rules; do not recreate prices, conditions or required options.
      const dpo = this.validationRoot.querySelector('#itoris_dynamicproductoptions') && window.dpoOptions;
      let dpoValid = true;
      if (typeof dpo?.validateForm === 'function') {
        try { dpoValid = dpo.validateForm(); }
        catch (error) { this.handleErrorMessage('Please check your product options and try again.'); return false; }
      }
      // Let DPO keep its invalid field in view before checking lower Uploadly fields.
      if (!dpoValid) return false;
      if (invalid) {
        const status = invalid.querySelector('.uploadcare--widget')?.getAttribute('data-status');
        const pending = this.pendingUploads.has(invalid) || status === 'uploading' || !!invalid.querySelector('[aria-busy="true"]');
        const message = status === 'error' ? 'Your photo could not be uploaded. Please try uploading it again.' :
          pending ? 'Please wait for your photo to finish uploading before adding to cart.' :
          'Please upload a photo so we can verify your seat.';
        this.uploadNotice = document.createElement('p');
        this.uploadNotice.setAttribute('role', 'alert');
        this.uploadNotice.className = 'uploadly-gate-notice';
        this.uploadNotice.textContent = message;
        invalid.append(this.uploadNotice);
        const target = invalid.querySelector('button, input:not([type="hidden"])') || this.uploadNotice;
        if (target === this.uploadNotice) target.tabIndex = -1;
        target.focus({ preventScroll: true });
        invalid.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return false;
      }
      return true;
    }

    /**
     * Resolves product id for ajaxProduct:added from add response and existing DOM/form.
     * @param {Object} json - Cart add API response
     * @returns {string|number|undefined}
     */
    getProductIdForEvent(json) {
      if (json?.product_id != null) return json.product_id;
      if (json?.items?.[0]?.product_id != null) return json.items[0].product_id;
      const fromPicker = this.querySelector('[data-product-id]')?.dataset?.productId;
      if (fromPicker != null && fromPicker !== '') return fromPicker;
      const fromParent = this.closest('[data-product]')?.dataset?.product;
      if (fromParent != null && fromParent !== '') return fromParent;
      const formId = this.form?.id ?? '';
      if (/^product-form-\d+$/.test(formId)) return formId.replace(/^product-form-/, '');
      if (formId.includes('-upsell-')) return formId.split('-upsell-')[1] ?? '';
      if (json?.id != null) return json.id;
      return undefined;
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
      return this.submitCart();
    }

    async submitCart(submissionData) {

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay')?.classList.remove('hidden');
      try {

        // Upsell items handled only in JS
        const upsell_ids_nodes = document.querySelectorAll(`[data-form="${this.form.getAttribute('id')}"] .upsell-id`);
        const upsell_quantities_nodes = document.querySelectorAll(`[data-form="${this.form.getAttribute('id')}"] .upsell-quantity`);
        let upsell_items = [];

        if (upsell_ids_nodes.length !== 0) {
          const upsell_ids = Array.from(upsell_ids_nodes).map(input => parseInt(input.value));
          const upsell_quantities = Array.from(upsell_quantities_nodes).map(input => parseInt(input.value));
          upsell_items = upsell_ids.map((id, index) => ({'id': id,'quantity': upsell_quantities[index]})).filter(item => item.quantity !== 0);
        }

        const config = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
          }
        }
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        const formData = submissionData || new FormData(this.form);
        this.validationRoot?.querySelectorAll('#photouploadapp [name^="properties["]').forEach(input => {
          if (!input.disabled && input.value) formData.set(input.name, input.value);
        });

        // If upsell items added refactor FormData
        if (upsell_items.length > 0) {
          this.multi_item_request = true;
          // Multipart keeps file properties as files, not JSON-serialized objects.
          const itemsData = new FormData();
          for (const [name, value] of formData) {
            if (name === 'id' || name === 'quantity' || name === 'selling_plan') {
              itemsData.append(`items[0][${name}]`, value);
            } else if (name.startsWith('properties[')) {
              itemsData.append(`items[0][properties][${name.slice(11, -1)}]`, value);
            } else if (name.startsWith('attributes[')) {
              itemsData.append(name, value);
            }
          }
          upsell_items.forEach((item, index) => {
            itemsData.append(`items[${index + 1}][id]`, item.id);
            itemsData.append(`items[${index + 1}][quantity]`, item.quantity);
          });
          config.body = itemsData;
        } else {
          this.multi_item_request = false;
          config.body = formData;
        }

        const res = await fetch(routes.cart_add_url + '.js', config)
        const json = await res.json();

        if (!res.ok) throw new Error(json.description);

        if (this.cartNotification) {
          const notificationTemplate = document.importNode(this.cartNotification.content, true);

          if (this.multi_item_request) {
            notificationTemplate.querySelector('[data-notification-title]').textContent = window.variantStrings.itemsAdded;

            json.items.forEach(item => {
              const singleItem = this.createNotificationItem(item);
              notificationTemplate.querySelector('[data-items]').appendChild(singleItem);
            });
          } else {
            notificationTemplate.querySelector('[data-notification-title]').textContent = window.variantStrings.itemAdded;
            const singleItem = this.createNotificationItem(json);
            notificationTemplate.querySelector('[data-items]').appendChild(singleItem);
          }

          if (document.querySelector('.cart-notification')) {
            document.querySelector('.cart-notification').remove();
          }

          document.body.appendChild(notificationTemplate);

          const the_notification = document.querySelector('.cart-notification');

          // Set focus to the notification for accessibility
          the_notification.setAttribute('tabindex', '-1');
          the_notification.focus();

          the_notification.querySelector('.notification-close').addEventListener('click', () => {
            the_notification.classList.remove('anim-in');
          });
          setTimeout(() => {
            the_notification.classList.add('anim-in');
          }, 50);
        }

        const productId = this.getProductIdForEvent(json);
        const event = new CustomEvent('ajaxProduct:added', { detail: productId != null ? { productId } : {} });
        document.dispatchEvent(event);
      } catch(error) {
        this.handleErrorMessage(error);
        console.error(error);
      } finally {
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading-overlay')?.classList.add('hidden');
      }
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper') || document.querySelector(`.product-form__error-message-wrapper[form="${this.form.getAttribute('id')}"]`)
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage.message || errorMessage;
      }
    }

    resetQuantityInput() {
      const hidden_quantity_input = document.querySelector('[type=hidden][name=quantity]');
      if (hidden_quantity_input) {
        hidden_quantity_input.value = 1;
      }
    }

    createNotificationItem(product) {
      const notificationItemTemplate = document.importNode(this.cartNotificationItem.content, true);

      const productImage = product.image || '';
      const filename = productImage.split('/').pop();
      const lastDotIndex = filename.lastIndexOf('.');
      const newFilename = `${filename.substring(0, lastDotIndex)}_120x${filename.substring(lastDotIndex)}`;
      const url = productImage.replace(filename, newFilename);

      notificationItemTemplate.querySelector('.notification-item-image').setAttribute('src', url);
      notificationItemTemplate.querySelector('.notification-item-image').classList.remove('hide');
      notificationItemTemplate.querySelector('.notification-item-title').textContent = product.product_title;

      product.options_with_values.forEach(item => {
        const row = document.createElement("dl");
        const nameCell = document.createElement("dt");
        const valueCell = document.createElement("dd");

        nameCell.textContent = item.name + ': ';
        valueCell.textContent = item.value;

        row.appendChild(nameCell);
        row.appendChild(valueCell);
        notificationItemTemplate.querySelector('.notification-item-details').appendChild(row);
      })

      const propertyKeyValues = Object.entries(product.properties || {});
      if (propertyKeyValues.length > 0) {
        const properties = document.createElement("dl");
        propertyKeyValues.forEach(([key, value]) => {
          const startsWith = key.startsWith('_');
          if (startsWith) return;
          const row = document.createElement("dl");
          const nameCell = document.createElement("dt");
          const valueCell = document.createElement("dd");

          nameCell.textContent = key + ': ';
          valueCell.textContent = value;

          row.appendChild(nameCell);
          row.appendChild(valueCell);
          properties.appendChild(row);
        });
        notificationItemTemplate.querySelector('.notification-item-details').appendChild(properties);
      }

      return notificationItemTemplate;
    }
  });
}

class AddMultiplier extends HTMLElement {
  constructor() {
    super();

    const multiplyButton = this.querySelector('button');
    this.submitEvent = new SubmitEvent("submit", { submitter: multiplyButton });
    this.multiplyBy = parseInt(this.querySelector('[name=multiplied-quantity]').value);
    this.productForm = this.closest('product-form').querySelector('form');
    const formId = this.productForm.getAttribute('id');
    this.quantity_input = document.querySelector(`[name=quantity][form=${ formId }]`);
    multiplyButton.addEventListener('click', this.submitProductForm.bind(this));
  }

  submitProductForm() {
    this.quantity_input.value = this.multiplyBy;
    this.productForm.dispatchEvent(this.submitEvent);
  }
}
customElements.define("add-multiplier", AddMultiplier);
